import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './InboundSyncImportBanner.css';

const STORAGE_KEY = 'kf_inbound_import_banner_collapsed';

/**
 * Collapsible banner shown while a SkySlope inbound import is running.
 * Guest vs subscriber copy from the inbound sync audit (IS-2).
 */
const InboundSyncImportBanner = ({
  visible,
  isGuest = false,
  propertyAddress = null,
}) => {
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return sessionStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    if (!visible) {
      return;
    }
    try {
      sessionStorage.setItem(STORAGE_KEY, collapsed ? '1' : '0');
    } catch {
      // ignore storage failures
    }
  }, [collapsed, visible]);

  if (!visible) {
    return null;
  }

  const addressLabel = (propertyAddress || '').trim();
  const guestBody = addressLabel
    ? `Now importing your ${addressLabel} deal from SkySlope so you can automatically update and close with KeyFlo. Start a free trial with KeyFlo to use it on all of your other deals.`
    : 'Now importing your deal from SkySlope so you can automatically update and close with KeyFlo. Start a free trial with KeyFlo to use it on all of your other deals.';
  const subscriberBody =
    'Now importing your active listings and pending sales from SkySlope so you can automatically update and close with KeyFlo.';
  const body = isGuest ? guestBody : subscriberBody;
  const collapsedLabel = isGuest
    ? (addressLabel ? `Importing ${addressLabel} from SkySlope…` : 'Importing your deal from SkySlope…')
    : 'Importing active listings and pending sales from SkySlope…';

  const toggleCollapsed = () => setCollapsed((prev) => !prev);

  return (
    <div
      className={`inbound-sync-import-banner${collapsed ? ' is-collapsed' : ''}`}
      role="status"
      aria-live="polite"
    >
      <button
        type="button"
        className="inbound-sync-import-banner-toggle"
        onClick={toggleCollapsed}
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand import banner' : 'Collapse import banner'}
      >
        <span className="inbound-sync-import-banner-chevron" aria-hidden="true">
          {collapsed ? '▸' : '▾'}
        </span>
        {collapsed ? (
          <span className="inbound-sync-import-banner-summary">{collapsedLabel}</span>
        ) : (
          <span className="inbound-sync-import-banner-title">SkySlope import in progress</span>
        )}
      </button>

      {!collapsed && (
        <div className="inbound-sync-import-banner-body">
          <p className="inbound-sync-import-banner-text">{body}</p>
          {isGuest && (
            <button
              type="button"
              className="inbound-sync-import-banner-cta"
              onClick={() => navigate('/profile?upgrade=1')}
            >
              Start free trial
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default InboundSyncImportBanner;
